import { forwardRef, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes, useId, useState } from "react";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import s from "./Field.module.scss";

type Ids = { id: string; describedBy?: string; invalid: boolean };

export const Field = ({ label, hint, error, optional, children }:
  { label: ReactNode; hint?: string; error?: ReactNode; optional?: string; children: (ids: Ids) => ReactNode }) => {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={s.field}>
      <label htmlFor={id} className={s.label}>{label}{optional && <span className={s.optional}> {optional}</span>}</label>
      {children({ id, describedBy, invalid: !!error })}
      {hint && <p id={hintId} className={s.hint}>{hint}</p>}
      {error && <p id={errId} className={s.error}><ErrorOutlineRoundedIcon aria-hidden className={s.errIcon} />{error}</p>}
    </div>
  );
};

type Invalid = { invalid?: boolean };

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & Invalid>(({ invalid, className, ...rest }, ref) => (
  <input ref={ref} aria-invalid={invalid || undefined} className={[s.control, className ?? ""].join(" ").trim()} {...rest} />
));
Input.displayName = "Input";

/**
 * A password box with an eye button (M3.2: on every password field). Always left-to-right, like the e-mail (M7.2), button
 * included, so it sits at the same end in Hebrew. The button is a real 48px button that says what it will do.
 */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & Invalid & { showLabel: string; hideLabel: string }>(
  ({ invalid, className, showLabel, hideLabel, ...rest }, ref) => {
    const [shown, setShown] = useState(false);
    return (
      <div className={s.password} dir="ltr">
        <input ref={ref} type={shown ? "text" : "password"} dir="ltr" spellCheck={false} autoCapitalize="none" aria-invalid={invalid || undefined}
          className={[s.control, s.passwordControl, className ?? ""].join(" ").trim()} {...rest} />
        <button type="button" className={s.eye} aria-label={shown ? hideLabel : showLabel} aria-pressed={shown} onClick={() => setShown((v) => !v)}>
          {shown ? <VisibilityOffRoundedIcon aria-hidden /> : <VisibilityRoundedIcon aria-hidden />}
        </button>
      </div>
    );
  });
PasswordInput.displayName = "PasswordInput";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & Invalid>(({ invalid, className, rows = 4, dir = "auto", ...rest }, ref) => (
  // Free text reads in the direction of what is typed (English on a Hebrew page keeps its full stop at the end).
  <textarea ref={ref} rows={rows} dir={dir} aria-invalid={invalid || undefined} className={[s.control, s.textarea, className ?? ""].join(" ").trim()} {...rest} />
));
Textarea.displayName = "Textarea";

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & Invalid>(({ invalid, className, children, ...rest }, ref) => (
  <select ref={ref} aria-invalid={invalid || undefined} className={[s.control, s.select, className ?? ""].join(" ").trim()} {...rest}>{children}</select>
));
Select.displayName = "Select";
